'use es6';

import { registerQuery, useQuery } from 'data-fetching-client';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { View } from 'foundations-components/components/View';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import { PAGE_KEY, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import Review from 'marketplace-ui-reviews/records/Review';
import ReviewStats from 'marketplace-ui-reviews/records/ReviewStats';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchUserHasProfile } from '../../clients/profilesClient';
import { getIsPublicApp } from '../../selectors/app';
import { getServices } from '../../slices/serviceCatalogSlice';
import ProfileReviewRenderer from './ProfileReviewRenderer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const GET_USER_HAS_PROFILE = registerQuery({
  fieldName: 'hasProfile',
  fetcher: fetchUserHasProfile
});
const ReviewsCardBody = ({
  onFiltersUpdate,
  onTrackInteraction,
  scrollToTabSection,
  reviewFilters,
  reviews,
  reviewStats,
  reviewsFetchStatus,
  reviewsTotal,
  reviewsCount
}) => {
  const {
    inline
  } = useResponsiveProps({
    inline: {
      xs: false,
      lg: true
    }
  });
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const catalogServices = useSelector(getServices);
  const isPublicApp = useSelector(getIsPublicApp);
  const {
    data,
    error,
    loading
  } = useQuery(GET_USER_HAS_PROFILE, {
    skip: isPublicApp
  });
  const hasProfile = useMemo(() => data ? data.hasProfile : false, [data]);
  const handleTrackInteraction = useCallback((action, extras = {}) => {
    onTrackInteraction(action, Object.assign({}, extras, {
      section: 'reviews'
    }));
  }, [onTrackInteraction]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    handleTrackInteraction(`change reviews ${textKey}`, {
      value: `${value}`
    });
    if (textKey === PAGE_KEY) {
      scrollToTabSection('reviews');
    }
    if (textKey === SEARCH_KEY) {
      setSearchSubmitted(value);
    }
    onFiltersUpdate({
      [textKey]: value
    });
  }, [handleTrackInteraction, onFiltersUpdate, scrollToTabSection]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      marginBlock: "600",
      children: /*#__PURE__*/_jsx(ReviewsListStats, {
        inline: inline,
        onUpdateFilters: onUpdateFilters,
        reviewStats: reviewStats,
        reviewType: ReviewTypes.PROFILE
      })
    }), /*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      reviewsCount: reviewsCount,
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      catalogServices: catalogServices,
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: getDataFetchingClientRequestState(error, loading),
      children: /*#__PURE__*/_jsx(ReviewsPaginatedList, {
        onUpdateFilters: onUpdateFilters,
        ReviewRenderer: ProfileReviewRenderer,
        reviewRendererProps: {
          hasProfile
        },
        reviews: reviews,
        reviewsFetchStatus: reviewsFetchStatus,
        reviewFilters: reviewFilters,
        reviewsTotal: reviewsTotal,
        searchTerm: searchSubmitted,
        onTrackInteraction: handleTrackInteraction
      })
    })]
  });
};
ReviewsCardBody.propTypes = {
  reviewFilters: PropTypes.object,
  reviews: PropTypes.arrayOf(PropTypes.shape(Review)).isRequired,
  reviewStats: PropTypes.shape(ReviewStats).isRequired,
  reviewsFetchStatus: PropTypes.oneOf(Object.values(RequestState)).isRequired,
  reviewsTotal: PropTypes.number.isRequired,
  reviewsCount: PropTypes.number.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  scrollToTabSection: PropTypes.func.isRequired
};
export default ReviewsCardBody;